import type { Editor } from "@tiptap/core";
import { omit } from "lodash";
import * as yup from "yup";
import { t } from "@/i18n-js/instance";
import { Button } from "@circle-react-shared/uikit/Button";
import { Form } from "@circle-react-shared/uikit/Form";
import { FormColorPicker } from "@circle-react-shared/uikit/Form/FormColorPicker";
import { FormInput } from "@circle-react-shared/uikit/Form/FormInput";
import { Item as FormItem } from "@circle-react-shared/uikit/Form/Item";
import { LinkInput } from "@circle-react-shared/uikit/Form/LinkInput";
import { prependProtocol } from "@circle-react-uikit/Form/LinkInput/prependProtocol";

const localeNamespace = "settings.emails.builder";

const getValidationSchema = (isWithinEmailEditor?: boolean) =>
  yup.object().shape({
    label: yup
      .string()
      .required(t([localeNamespace, "errors.label_is_required"])),
    url: yup
      .string()
      .transform((_, originalValue) =>
        prependProtocol(originalValue, { allowMailTo: !isWithinEmailEditor }),
      )
      .test(
        "is-url-or-mailto",
        t([localeNamespace, "errors.url_is_invalid"]),
        value => {
          if (!value) return true;
          if (
            !isWithinEmailEditor &&
            value.toLowerCase().startsWith("mailto:")
          ) {
            return yup
              .string()
              .email()
              .isValidSync(value.replace(/^mailto:/i, ""));
          }
          return yup.string().url().isValidSync(value);
        },
      )
      .required(t([localeNamespace, "errors.url_is_required"])),
    color: yup
      .string()
      .matches(
        /^#[0-9A-Fa-f]{6}$/,
        t([localeNamespace, "errors.color_is_required"]),
      )
      .required(t([localeNamespace, "errors.color_is_required"])),
    ...(isWithinEmailEditor && {
      text_color: yup
        .string()
        .matches(
          /^#[0-9A-Fa-f]{6}$/,
          t([localeNamespace, "errors.color_is_required"]),
        )
        .required(t([localeNamespace, "errors.color_is_required"])),
    }),
  });

interface ButtonFormProps {
  defaultValues: {
    label: string;
    url: string;
    color: string;
    text_color: string;
  };
  editor: Editor;
  inputRef: any;
  isWithinEmailEditor?: boolean;
  onClose: () => void;
  range: {
    from: number;
    to: number;
  };
}

export function ButtonForm({
  inputRef,
  editor,
  range,
  onClose,
  defaultValues,
  isWithinEmailEditor,
}: ButtonFormProps) {
  const isEdit = !!defaultValues.label;

  return (
    <Form
      defaultValues={defaultValues}
      onSubmit={attrs => {
        editor
          .chain()
          .focus()
          .insertContentAt(
            range,
            [
              {
                type: "cta",
                attrs: isWithinEmailEditor ? attrs : omit(attrs, "text_color"),
              },
              !isEdit && { type: "hardBreak" },
            ].filter(Boolean),
          )
          .run();
        onClose();
      }}
      validationSchema={getValidationSchema(isWithinEmailEditor)}
      shouldStopPropagation
      noValidate
    >
      <div className="flex flex-col pb-4">
        <FormItem
          name="label"
          hidePlaceholder
          hideDescription
          hideBorder
          label={t("email_editor.cta.label")}
          required
        >
          <FormInput
            ref={inputRef}
            name="label"
            label={t("email_editor.cta.label")}
            placeholder={t("email_editor.cta.label")}
            autocomplete="off"
          />
        </FormItem>
        <FormItem
          name="url"
          hidePlaceholder
          hideDescription
          hideBorder
          label={t("email_editor.cta.url")}
          required
        >
          <LinkInput
            allowMailTo={!isWithinEmailEditor}
            label={t("email_editor.cta.url")}
            name="url"
            autocomplete="off"
          />
        </FormItem>
        <FormItem
          name="color"
          hidePlaceholder
          hideDescription
          hideBorder
          label={t("email_editor.cta.color")}
          required
        >
          <FormColorPicker name="color" />
        </FormItem>
        {isWithinEmailEditor && (
          <FormItem
            name="text_color"
            hidePlaceholder
            hideDescription
            hideBorder
            label={t("email_editor.cta.text_color")}
            required
          >
            <FormColorPicker name="text_color" />
          </FormItem>
        )}
      </div>
      <div className="flex justify-end gap-x-3 pb-5 pt-2.5">
        <Button type="button" onClick={onClose} variant="secondary-light">
          {t("cancel")}
        </Button>
        <Button type="submit" variant="circle">
          {t("email_editor.cta.save")}
        </Button>
      </div>
    </Form>
  );
}
